import React, { useMemo, useState } from "react";
import Image from "../assets/Ragister.png";

// Spring Boot Backend Base URL
// const BASE_URL = "http://localhost:9091/api/auth";
const BASE_URL = "https://api.mediitacademy.com/api/auth";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.194 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 16.108 19.001 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.144 35.091 26.715 36 24 36c-5.173 0-9.625-3.329-11.287-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.084 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    gender: "",
    dob: "",
    studentClass: "",
    course: "",
    email: "",
    emailOtp: "",
    mobile: "",
    mobileOtp: "",
  });

  // Verification States
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  // Dynamic Error/Status States for Input Feedback
  const [emailStatus, setEmailStatus] = useState<{ text: string; isError: boolean } | null>(null);
  const [mobileStatus, setMobileStatus] = useState<{ text: string; isError: boolean } | null>(null);
  const [formStatus, setFormStatus] = useState<{ text: string; isError: boolean } | null>(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const isEmailValid = emailRegex.test(formData.email);
  const isMobileValid = formData.mobile.length === 10;
  const shouldShowCourse = formData.studentClass === "others";

  const inputClass =
    "mt-1.5 h-10 w-full rounded-lg border border-slate-300 px-3 text-[13px] outline-none transition-all focus:border-blue-600 focus:ring-3 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed";

  const selectClass =
    "mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13px] outline-none transition-all focus:border-blue-600 focus:ring-3 focus:ring-blue-50 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed";

  const isFormValid = useMemo(() => {
    const requiredFields =
      formData.fullName.trim() !== "" &&
      formData.gender !== "" &&
      formData.dob !== "" &&
      formData.studentClass !== "" &&
      isEmailVerified &&
      isMobileVerified;

    if (shouldShowCourse) {
      return requiredFields && formData.course !== "";
    }
    return requiredFields;
  }, [formData, isEmailVerified, isMobileVerified, shouldShowCourse]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const onlyDigits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: onlyDigits, mobileOtp: "" }));
      setMobileOtpSent(false);
      setIsMobileVerified(false);
      setMobileStatus(null);
      return;
    }

    if (name === "email") {
      setFormData((prev) => ({ ...prev, email: value, emailOtp: "", mobile: "", mobileOtp: "" }));
      setEmailOtpSent(false);
      setIsEmailVerified(false);
      setMobileOtpSent(false);
      setIsMobileVerified(false);
      setEmailStatus(null);
      setMobileStatus(null);
      return;
    }

    if (name === "studentClass") {
      setFormData((prev) => ({
        ...prev,
        studentClass: value,
        course: value === "others" ? prev.course : "",
      }));
      return;
    }

    if (name === "emailOtp" || name === "mobileOtp") {
      const onlyDigits = value.replace(/\D/g, "").slice(0, 6);
      setFormData((prev) => ({ ...prev, [name]: onlyDigits }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 1. Send Email OTP (Backend check dynamic handler)
  const sendEmailOtp = async () => {
    if (!isEmailValid) return;
    setEmailStatus({ text: "Sending OTP...", isError: false });
    try {
      const response = await fetch(`${BASE_URL}/send-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmailOtpSent(true);
        setEmailStatus({ text: data.message || "OTP sent successfully!", isError: false });
      } else {
        // "This email is already registered." handler
        setEmailStatus({ text: data.message || "Failed to send Email OTP.", isError: true });
      }
    } catch (error) {
      setEmailStatus({ text: "Server connection failed.", isError: true });
    }
  };

  // 2. Verify Email OTP
  const verifyEmailOtp = async () => {
    if (!formData.emailOtp) return;
    try {
      const response = await fetch(`${BASE_URL}/verify-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: formData.emailOtp }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsEmailVerified(true);
        setEmailStatus({ text: data.message || "Email verified successfully!", isError: false });
      } else {
        setEmailStatus({ text: data.message || "Invalid or Expired Email OTP.", isError: true });
      }
    } catch (error) {
      setEmailStatus({ text: "Verification server error.", isError: true });
    }
  };

  // 3. Send Mobile OTP (Backend masked email handler)
  const sendMobileOtp = async () => {
    if (!isMobileValid || !isEmailVerified) return;
    setMobileStatus({ text: "Sending OTP...", isError: false });
    try {
      const response = await fetch(`${BASE_URL}/send-mobile-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          mobileNumber: formData.mobile,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMobileOtpSent(true);
        setMobileStatus({ text: data.message || "Mobile OTP sent successfully!", isError: false });
      } else {
     
        setMobileStatus({ text: data.message || "Failed to send Mobile OTP.", isError: true });
      }
    } catch (error) {
      setMobileStatus({ text: "Server error sending mobile OTP.", isError: true });
    }
  };

  // 4. Verify Mobile OTP
  const verifyMobileOtp = async () => {
    if (!formData.mobileOtp) return;
    try {
      const response = await fetch(`${BASE_URL}/verify-mobile-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, otp: formData.mobileOtp }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsMobileVerified(true);
        setMobileStatus({ text: data.message || "Mobile verified successfully!", isError: false });
      } else {
        setMobileStatus({ text: data.message || "Invalid or Expired Mobile OTP.", isError: true });
      }
    } catch (error) {
      setMobileStatus({ text: "Verification server error.", isError: true });
    }
  };

  // 5. Final Submit Registration
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setFormStatus(null);
    try {
      const response = await fetch(`${BASE_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName,
          gender: formData.gender,
          dob: formData.dob,
          email: formData.email,
          mobileNumber: formData.mobile,
          course: shouldShowCourse ? formData.course : "",
          school: formData.studentClass,
        }),
      });

      const data = await response.json();

      if (response.ok && data.message === "Success") {
        setFormStatus({ text: "Account Created Successfully!", isError: false });
      } else {
        setFormStatus({ text: data.message || "Registration failed!", isError: true });
      }
    } catch (error) {
      setFormStatus({ text: "Could not connect to the registration server.", isError: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen overflow-hidden bg-cover bg-center bg-no-repeat text-[#101827] font-['Inter']"
      style={{
        backgroundImage:
          "url('https://img.freepik.com/premium-vector/modern-simple-blue-gold-abstract-background-suitable-business-card-presentation-flyer-brochure-print-certificate-template_181182-16594.jpg')",
      }}
    >
      <div className="mx-auto grid h-screen max-w-[1280px] grid-cols-1 lg:grid-cols-2 lg:gap-8">
        
        {/* LEFT SIDE: ORIGINAL IMAGE VIEWPORT UNTOUCHED */}
        <div className="relative hidden h-screen items-center justify-center overflow-hidden px-6 lg:flex">
          <div className="absolute left-16 top-20 h-28 w-28 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute bottom-16 right-20 h-36 w-36 rounded-full bg-orange-100 blur-3xl" />

          <div className="relative flex max-w-[540px] flex-col items-center text-center">
            <div className="mb-5 text-center">
              <h2 className="max-w-xl text-3xl font-black leading-tight tracking-[-0.04em] text-slate-950 xl:text-4xl">
                Start your learning journey with Mediit Academy
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-6 text-slate-600">
                Join live classes, get mentor support, attempt mock tests and track your learning progress from one simple dashboard.
              </p>
            </div>

            <div className="relative h-[420px] w-[450px]">
              <div className="absolute inset-x-8 bottom-2 h-[340px] rounded-full border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.12)]" />
              
              <div className="absolute left-1/2 top-[-24px] h-[440px] w-[440px] -translate-x-1/2 overflow-visible">
                <img
                  src={Image}
                  alt="Student learning online"
                  className="h-full w-full scale-[1.22] object-contain object-center"
                />
              </div>

              <div className="absolute left-5 top-24 rounded-2xl bg-white px-4 py-3 text-left shadow-xl">
                <p className="text-sm font-black text-slate-950">Live Classes</p>
                <p className="mt-1 text-xs text-slate-500">Learn from expert mentors</p>
              </div>

              <div className="absolute bottom-10 right-2 rounded-2xl bg-white px-4 py-3 text-left shadow-xl">
                <p className="text-sm font-black text-slate-950">Mock Tests</p>
                <p className="mt-1 text-xs text-slate-500">Practice with real exam pattern</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: ORIGINAL FORM AREA UNTOUCHED */}
        <div className="flex h-screen items-center justify-center px-4 py-4 sm:px-6">
          <div className="w-full max-w-[455px] rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            <div className="mb-4 text-center lg:text-left">
              <a href="/" className="mb-4 inline-flex items-center text-xs font-bold text-blue-600 hover:underline">
                ← Back to home
              </a>
              <h1 className="text-3xl font-black tracking-[-0.04em] text-slate-950">Create account</h1>
              <p className="mt-1.5 text-sm leading-5 text-slate-500">
                Fill student details and verify email/mobile with OTP.
              </p>
            </div>

            <form onSubmit={handleRegister} className="space-y-2.5">
              {/* FULL NAME */}
              <div>
                <label className="text-xs font-black text-slate-800">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter student full name"
                  className={inputClass}
                  required
                />
              </div>

              {/* GENDER + DOB */}
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-black text-slate-800">Gender</label>
                  <select name="gender" value={formData.gender} onChange={handleChange} className={selectClass} required>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-black text-slate-800">DOB</label>
                  <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={inputClass} required />
                </div>
              </div>

              {/* CLASS + COURSE */}
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <div className={shouldShowCourse ? "" : "sm:col-span-2"}>
                  <label className="text-xs font-black text-slate-800">Student Class</label>
                  <select name="studentClass" value={formData.studentClass} onChange={handleChange} className={selectClass} required>
                    <option value="">Select class</option>
                    <option value="Class 7">Class 7</option>
                    <option value="Class 8">Class 8</option>
                    <option value="Class 9">Class 9</option>
                    <option value="Class 10">Class 10</option>
                    <option value="Class 11">Class 11</option>
                    <option value="Class 12">Class 12</option>
                    <option value="others">Others</option>
                  </select>
                </div>

                {shouldShowCourse && (
                  <div>
                    <label className="text-xs font-black text-slate-800">Course</label>
                    <select name="course" value={formData.course} onChange={handleChange} className={selectClass} required={shouldShowCourse}>
                      <option value="">Select course</option>
                      <option value="JEE Mains">JEE Mains</option>
                      <option value="JEE Advanced">JEE Advanced</option>
                      <option value="NEET">NEET</option>
                      <option value="Foundation">Foundation</option>
                      <option value="Dropper Batch">Dropper Batch</option>
                    </select>
                  </div>
                )}
              </div>

              {/* EMAIL SECTION */}
              <div>
                <label className="text-xs font-black text-slate-800">Email Address</label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    type="email"
                    name="email"
                    disabled={isEmailVerified}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    className={`h-10 flex-1 rounded-lg border px-3 text-[13px] outline-none transition-all focus:ring-3 ${
                      (formData.email && !isEmailValid) || (emailStatus && emailStatus.isError)
                        ? "border-red-400 focus:border-red-500 focus:ring-red-50"
                        : "border-slate-300 focus:border-blue-600 focus:ring-blue-50"
                    } disabled:bg-slate-50 disabled:text-slate-500`}
                    required
                  />
                  <button
                    type="button"
                    onClick={sendEmailOtp}
                    disabled={!isEmailValid || isEmailVerified}
                    className={`h-10 shrink-0 rounded-lg border px-3 text-xs font-black transition-all ${
                      isEmailValid && !isEmailVerified
                        ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                        : "cursor-not-allowed border-slate-300 text-slate-400"
                    }`}
                  >
                    {isEmailVerified ? "Verified ✓" : emailOtpSent ? "Resend" : "Send OTP"}
                  </button>
                </div>

                {emailStatus && (
                  <p className={`mt-1 text-[11px] font-semibold ${emailStatus.isError ? "text-red-500" : "text-green-600"}`}>
                    {emailStatus.isError ? "⚠️ " : ""}{emailStatus.text}
                  </p>
                )}

                {emailOtpSent && !isEmailVerified && (
                  <div className="mt-1.5 flex gap-2">
                    <input
                      type="text"
                      name="emailOtp"
                      value={formData.emailOtp}
                      onChange={handleChange}
                      inputMode="numeric"
                      placeholder="Enter email OTP"
                      className="h-10 flex-1 rounded-lg border border-slate-300 px-3 text-[13px] outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={verifyEmailOtp}
                      disabled={formData.emailOtp.length < 4}
                      className="h-10 bg-blue-600 text-white rounded-lg px-4 text-xs font-black hover:bg-blue-700 disabled:bg-slate-300"
                    >
                      Verify
                    </button>
                  </div>
                )}
              </div>

              {/* MOBILE SECTION */}
              <div>
                <label className={`text-xs font-black ${isEmailVerified ? "text-slate-800" : "text-slate-400"}`}>
                  Mobile Number {!isEmailVerified && "(Verify Email First)"}
                </label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    disabled={!isEmailVerified || isMobileVerified}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10 digit mobile number"
                    className={`h-10 flex-1 rounded-lg border px-3 text-[13px] outline-none transition-all focus:ring-3 ${
                      (formData.mobile && !isMobileValid) || (mobileStatus && mobileStatus.isError)
                        ? "border-red-400 focus:border-red-500 focus:ring-red-50"
                        : "border-slate-300 focus:border-blue-600 focus:ring-blue-50"
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={sendMobileOtp}
                    disabled={!isMobileValid || !isEmailVerified || isMobileVerified}
                    className={`h-10 shrink-0 rounded-lg border px-3 text-xs font-black transition-all ${
                      isMobileValid && isEmailVerified && !isMobileVerified
                        ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                        : "cursor-not-allowed border-slate-300 text-slate-400"
                    }`}
                  >
                    {isMobileVerified ? "Verified ✓" : mobileOtpSent ? "Resend" : "Send OTP"}
                  </button>
                </div>

                {mobileStatus && (
                  <p className={`mt-1 text-[11px] font-semibold ${mobileStatus.isError ? "text-red-500" : "text-green-600"}`}>
                    {mobileStatus.isError ? "⚠️ " : ""}{mobileStatus.text}
                  </p>
                )}

                {mobileOtpSent && !isMobileVerified && (
                  <div className="mt-1.5 flex gap-2">
                    <input
                      type="text"
                      name="mobileOtp"
                      value={formData.mobileOtp}
                      onChange={handleChange}
                      inputMode="numeric"
                      placeholder="Enter mobile OTP"
                      className="h-10 flex-1 rounded-lg border border-slate-300 px-3 text-[13px] outline-none"
                      required
                    />
                    <button
                      type="button"
                      onClick={verifyMobileOtp}
                      disabled={formData.mobileOtp.length < 4}
                      className="h-10 bg-blue-600 text-white rounded-lg px-4 text-xs font-black hover:bg-blue-700 disabled:bg-slate-300"
                    >
                      Verify
                    </button>
                  </div>
                )}
              </div>

              {formStatus && (
                <div className={`p-2 rounded-lg text-[11px] font-bold ${formStatus.isError ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
                  {formStatus.text}
                </div>
              )}

              <button
                type="submit"
                disabled={!isFormValid || loading}
                className={`h-10 w-full rounded-lg text-xs font-black shadow-lg transition-all ${
                  isFormValid && !loading
                    ? "bg-[#101827] text-white hover:-translate-y-0.5 hover:bg-slate-800"
                    : "cursor-not-allowed bg-slate-300 text-slate-500 shadow-none"
                }`}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <div className="my-2 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[11px] font-medium text-slate-400">or continue with</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <button
              type="button"
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-800 transition-all hover:border-slate-400 hover:bg-slate-50"
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <p className="mt-2 text-center text-xs text-slate-600">
              Already have an account?{" "}
              <a href="/login" className="font-black text-blue-600">
                Login here
              </a>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RegisterPage;